import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Lock, Check, Building2, CreditCard, Map, Globe2, Users, Copy, ArrowRight, AlertTriangle, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * The onboarding hub — where a personal workspace becomes a working estate.
 *
 * Presented as a checklist rather than a forced wizard: these steps have a real dependency
 * order (claim gates plan gates invitations) but a volunteer director will do them across
 * several sittings, not in one. The checklist shows where they are without trapping them
 * in a flow they can't leave.
 *
 * Each step states *why* it exists. "Claim this workspace" is meaningless on its own; "your
 * management company is responsible for residents' data, so a director has to take this on
 * before anyone can be invited" is a reason someone can act on.
 */

const STEP = {
    done: "bg-emerald-50 border-emerald-200 text-emerald-900",
    active: "bg-white border-slate-300",
    locked: "bg-slate-50 border-slate-200 text-slate-500",
};

function StepCard({ icon: Icon, n, title, why, state, children }) {
    return (
        <div className={`rounded-lg border p-5 ${STEP[state]}`} data-testid={`onboarding-step-${n}`}>
            <div className="flex items-start gap-3">
                <div
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        state === "done"
                            ? "bg-emerald-600 text-white"
                            : state === "locked"
                            ? "bg-slate-200 text-slate-500"
                            : "bg-slate-900 text-white"
                    }`}
                >
                    {state === "done" ? <Check size={16} /> : n}
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-semibold text-base flex items-center gap-2">
                        <Icon size={16} className="opacity-70" />
                        {title}
                    </h3>
                    <p className="text-sm mt-1 opacity-80 leading-relaxed">{why}</p>
                    {children && <div className="mt-4">{children}</div>}
                </div>
            </div>
        </div>
    );
}

export default function Onboarding() {
    const navigate = useNavigate();
    const [ws, setWs] = useState(null);
    const [plans, setPlans] = useState([]);
    const [billingMode, setBillingMode] = useState("manual");
    const [codes, setCodes] = useState([]);
    const [localities, setLocalities] = useState([]);
    const [busy, setBusy] = useState(false);
    const planFileRef = useRef(null);

    const [claim, setClaim] = useState({
        organisation_name: "",
        company_number: "",
        unit_count: "",
        claim_evidence: "",
    });
    const [localityName, setLocalityName] = useState("");
    const [postcode, setPostcode] = useState("");
    const [postcodeHit, setPostcodeHit] = useState(null);
    const [postcodeMiss, setPostcodeMiss] = useState(false);
    const [gazetteer, setGazetteer] = useState([]);
    const [dates, setDates] = useState({ company_review_date: "", accounting_reference_date: "" });

    const load = useCallback(async () => {
        try {
            const [w, p] = await Promise.all([
                api.get("/onboarding/workspace"),
                api.get("/plans"),
            ]);
            setWs(w.data);
            setPlans(p.data.plans);
            setBillingMode(p.data.billing_mode);
            if (w.data.can_invite) {
                const c = await api.get("/onboarding/join-codes");
                setCodes(c.data);
            }
            const l = await api.get("/localities");
            setLocalities(l.data.localities || []);
            setGazetteer(l.data.gazetteer || []);
            try {
                const st = await api.get("/estate/settings");
                setDates({
                    company_review_date: st.data.company_review_date || "",
                    accounting_reference_date: st.data.accounting_reference_date || "",
                });
            } catch {
                // A personal workspace may not have settings yet — not an error worth showing.
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load your workspace");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const q = localityName.trim();
        if (q.length < 2) return undefined;
        let cancelled = false;
        api.get("/civic/suggest", { params: { q } })
            .then((r) => {
                if (!cancelled) setGazetteer(r.data.units || []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [localityName]);

    const doClaim = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/onboarding/claim", {
                ...claim,
                unit_count: claim.unit_count ? Number(claim.unit_count) : null,
                company_number: claim.company_number || null,
            });
            toast.success("Workspace claimed for your management company");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not claim this workspace");
        } finally {
            setBusy(false);
        }
    };

    const choosePlan = async (planId) => {
        setBusy(true);
        try {
            const res = await api.post(`/onboarding/checkout?plan_id=${planId}`);
            if (res.data.checkout_url) {
                window.location.href = res.data.checkout_url;
                return;
            }
            // No payment provider configured: activation is an explicit step rather than
            // something the app can grant itself.
            await api.post(`/onboarding/activate?plan_id=${planId}`);
            toast.success("Plan activated");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
        } finally {
            setBusy(false);
        }
    };

    const uploadPlan = async () => {
        const file = planFileRef.current?.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const res = await api.post("/estate/plan/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                res.data.converted
                    ? `Site plan converted from ${res.data.source_format.toUpperCase()} and saved`
                    : "Site plan saved"
            );
            if (planFileRef.current) planFileRef.current.value = "";
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not read that plan");
        } finally {
            setBusy(false);
        }
    };

    const makeSchematic = async () => {
        setBusy(true);
        try {
            await api.post(
                `/estate/plan/schematic?unit_count=${ws?.unit_count || 20}&layout=close`
            );
            toast.success("Placeholder schematic created — replace it with your real plan later");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not generate a schematic");
        } finally {
            setBusy(false);
        }
    };

    const lookupPostcode = async () => {
        const q = postcode.trim();
        if (!q) return;
        setBusy(true);
        setPostcodeMiss(false);
        try {
            const res = await api.get("/civic/postcode", { params: { q } });
            const hit = res.data.hit;
            if (hit) {
                setPostcodeHit(hit);
                setLocalityName(hit.principal_name);
            } else {
                setPostcodeHit(null);
                setPostcodeMiss(true);
            }
        } catch (err) {
            setPostcodeHit(null);
            setPostcodeMiss(true);
            toast.error(err?.response?.data?.detail || "Could not look up that postcode");
        } finally {
            setBusy(false);
        }
    };

    const joinLocality = async () => {
        if (!localityName.trim()) return;
        setBusy(true);
        try {
            const existing = localities.find(
                (l) => l.name.toLowerCase() === localityName.trim().toLowerCase()
            );
            const id = existing
                ? existing.id
                : (await api.post("/localities", { name: localityName.trim() })).data.id;
            await api.post(`/estate/locality?locality_id=${id}&sharing_enabled=true`);
            toast.success(`Joined ${localityName.trim()}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not join that locality");
        } finally {
            setBusy(false);
        }
    };

    const saveDates = async () => {
        setBusy(true);
        try {
            await api.put("/estate/settings", {
                company_review_date: dates.company_review_date || null,
                accounting_reference_date: dates.accounting_reference_date || null,
            });
            toast.success("Key dates saved — your deadlines will now show under Guidance");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save those dates");
        } finally {
            setBusy(false);
        }
    };

    const newCode = async () => {
        setBusy(true);
        try {
            await api.post("/onboarding/join-codes?expires_in_days=30");
            toast.success("Join code created");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create a join code");
        } finally {
            setBusy(false);
        }
    };

    if (!ws) {
        return <div className="p-8 text-sm text-slate-500">Loading your workspace…</div>;
    }

    const claimed = ws.claim_status === "claimed";
    const paid = ws.plan !== "free";
    const live = codes.filter((c) => !c.is_revoked);

    return (
        <div className="max-w-3xl mx-auto px-6 py-10" data-testid="onboarding-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <Building2 size={14} /> {ws.name}
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">
                Set up your estate
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                You can do these in any sitting — nothing is lost if you stop halfway. Until step 1 is
                done, this workspace holds only your own documents.
            </p>

            <div className="mt-8 space-y-4">
                {/* 1 — claim */}
                <StepCard
                    icon={Building2}
                    n={1}
                    title="Take this on for your management company"
                    why={
                        claimed
                            ? "Done. Your management company is now responsible for this workspace."
                            : "Your management company is responsible for residents' data — so a director has to take this on before anyone else can be invited."
                    }
                    state={claimed ? "done" : "active"}
                >
                    {!claimed && (
                        <form onSubmit={doClaim} className="space-y-3">
                            <div>
                                <Label htmlFor="org">Management company name</Label>
                                <Input
                                    id="org"
                                    required
                                    value={claim.organisation_name}
                                    onChange={(e) => setClaim({ ...claim, organisation_name: e.target.value })}
                                    placeholder="Richardson Close Residents Management Company Ltd"
                                    data-testid="claim-org-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="cn">Company number (optional)</Label>
                                    <Input
                                        id="cn"
                                        value={claim.company_number}
                                        onChange={(e) => setClaim({ ...claim, company_number: e.target.value })}
                                        placeholder="12345678"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="uc">How many homes?</Label>
                                    <Input
                                        id="uc"
                                        type="number"
                                        min="1"
                                        value={claim.unit_count}
                                        onChange={(e) => setClaim({ ...claim, unit_count: e.target.value })}
                                        placeholder="33"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="ev">How can you show you're a director?</Label>
                                <Textarea
                                    id="ev"
                                    required
                                    rows={2}
                                    value={claim.claim_evidence}
                                    onChange={(e) => setClaim({ ...claim, claim_evidence: e.target.value })}
                                    placeholder="Named as a director on the Companies House register."
                                    data-testid="claim-evidence-input"
                                />
                                <p className="text-xs text-slate-500 mt-1.5">
                                    Recorded with your account. We don't verify this automatically — it's kept
                                    so it can be checked if ever questioned.
                                </p>
                            </div>
                            <Button
                                type="submit"
                                disabled={busy}
                                className="bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid="claim-submit-button"
                            >
                                Take this on <ArrowRight size={15} className="ml-1.5" />
                            </Button>
                        </form>
                    )}
                </StepCard>

                {/* 2 — plan */}
                <StepCard
                    icon={CreditCard}
                    n={2}
                    title="Choose how your estate uses this"
                    why={
                        paid
                            ? `You're on the ${ws.plan} plan.`
                            : "Working on your own documents is free. Sharing with your neighbours needs a plan for the estate."
                    }
                    state={paid ? "done" : claimed ? "active" : "locked"}
                >
                    {claimed && !paid && (
                        <>
                            <div className="grid sm:grid-cols-3 gap-3">
                                {plans.map((p) => (
                                    <div
                                        key={p.id}
                                        className={`rounded-md border p-3.5 flex flex-col ${
                                            p.id === "free" ? "border-slate-200 bg-slate-50" : "border-slate-300 bg-white"
                                        }`}
                                        data-testid={`plan-card-${p.id}`}
                                    >
                                        <div className="font-semibold text-sm">{p.name}</div>
                                        <div className="text-lg font-bold mt-1 tabular-nums">
                                            {p.price_pence === 0
                                                ? "Free"
                                                : `£${(p.price_pence / 100).toFixed(0)}`}
                                            {p.interval === "year" && (
                                                <span className="text-xs font-normal text-slate-500">/year</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-600 mt-1.5 flex-1">{p.summary}</p>
                                        {p.id !== "free" && (
                                            <Button
                                                onClick={() => choosePlan(p.id)}
                                                disabled={busy}
                                                className="mt-3 w-full bg-slate-900 hover:bg-slate-800 text-white text-xs h-8"
                                                data-testid={`plan-choose-${p.id}`}
                                            >
                                                Choose
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {billingMode === "manual" && (
                                <p className="text-xs text-amber-700 mt-3 flex gap-1.5">
                                    <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                    Card payment isn't switched on yet — choosing a plan activates it directly
                                    for now.
                                </p>
                            )}
                        </>
                    )}
                </StepCard>

                {/* 3 — site plan */}
                <StepCard
                    icon={Map}
                    n={3}
                    title="Add your estate's site plan"
                    why="So residents can point at exactly where something happened. A PDF from your deeds or the developer works — we'll convert it."
                    state={claimed ? "active" : "locked"}
                >
                    {claimed && (
                        <div className="flex flex-wrap items-center gap-2">
                            <input
                                ref={planFileRef}
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.webp"
                                className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-900 file:text-white"
                                data-testid="plan-file-input"
                            />
                            <Button onClick={uploadPlan} disabled={busy} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white">
                                Upload plan
                            </Button>
                            <span className="text-xs text-slate-400">or</span>
                            <Button onClick={makeSchematic} disabled={busy} variant="outline" className="h-8 text-xs">
                                I don't have one
                            </Button>
                        </div>
                    )}
                </StepCard>

                {/* 4 — locality */}
                <StepCard
                    icon={Globe2}
                    n={4}
                    title="Join your local area"
                    why="Share regulations and council guidance with nearby estates — and use what they've already found. Only reference material is ever shared; your own documents never are."
                    state={claimed ? "active" : "locked"}
                >
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-2 items-end">
                                <div className="flex-1 min-w-[180px]">
                                    <Label htmlFor="pc">Estate postcode</Label>
                                    <Input
                                        id="pc"
                                        value={postcode}
                                        onChange={(e) => setPostcode(e.target.value)}
                                        placeholder="e.g. HP19 7AA"
                                        data-testid="postcode-input"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={lookupPostcode}
                                    disabled={busy || !postcode.trim()}
                                    className="h-9 text-xs"
                                    data-testid="postcode-lookup-button"
                                >
                                    Look up council
                                </Button>
                            </div>
                            {postcodeHit && (
                                <p className="text-xs text-slate-600 m-0" data-testid="postcode-hit">
                                    Matched {postcodeHit.principal_name} ({postcodeHit.gss_code}).
                                    Join below to attach this estate. Unknown postcodes stay unmatched.
                                </p>
                            )}
                            {postcodeMiss && (
                                <p className="text-xs text-slate-600 m-0" data-testid="postcode-miss">
                                    No seed match for that outward code. Type the live council, town or parish
                                    below — we do not guess a district.
                                </p>
                            )}
                            <div className="flex flex-wrap gap-2 items-end">
                            <div className="flex-1 min-w-[220px]">
                                <Label htmlFor="loc">Council, town or parish</Label>
                                <Input
                                    id="loc"
                                    list="locality-options"
                                    value={localityName}
                                    onChange={(e) => setLocalityName(e.target.value)}
                                    placeholder="e.g. Aylesbury Town Council"
                                    data-testid="locality-input"
                                />
                                <datalist id="locality-options">
                                    {gazetteer.map((u) => (
                                        <option key={u.id} value={u.name}>
                                            {u.kind} · {u.principal_name || ""}
                                        </option>
                                    ))}
                                    {localities.map((l) => (
                                        <option key={l.id} value={l.name} />
                                    ))}
                                </datalist>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Aylesbury Vale District no longer exists — join Buckinghamshire Council or
                                    the town/parish the houses sit in.
                                </p>
                            </div>
                            <Button onClick={joinLocality} disabled={busy || !claimed || !localityName.trim()} className="h-9 text-xs bg-slate-900 hover:bg-slate-800 text-white">
                                Join
                            </Button>
                            </div>
                            {!claimed && (
                                <p className="text-[11px] text-slate-500 m-0">
                                    Claim the workspace before joining a council. Lookup still works.
                                </p>
                            )}
                        </div>
                </StepCard>

                {/* 5 — invite */}
                <StepCard
                    icon={Users}
                    n={5}
                    title="Invite your neighbours"
                    why={
                        paid
                            ? "Share this code however you already talk to each other — WhatsApp, a noticeboard, the AGM."
                            : "Available once your estate is on a plan."
                    }
                    state={live.length ? "done" : paid ? "active" : "locked"}
                >
                    {paid && (
                        <div className="space-y-2">
                            {live.map((c) => (
                                <div
                                    key={c.id}
                                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2"
                                    data-testid={`join-code-${c.code}`}
                                >
                                    <code className="font-mono text-sm flex-1">{c.code}</code>
                                    <span className="text-xs text-slate-500 tabular-nums">
                                        used {c.use_count}
                                        {c.max_uses ? `/${c.max_uses}` : ""}
                                    </span>
                                    <Button
                                        variant="outline"
                                        className="h-7 text-xs"
                                        onClick={() => {
                                            navigator.clipboard?.writeText(
                                                `${window.location.origin}/join/${c.code}`
                                            );
                                            toast.success("Join link copied");
                                        }}
                                    >
                                        <Copy size={12} className="mr-1" /> Copy link
                                    </Button>
                                </div>
                            ))}
                            <Button onClick={newCode} disabled={busy} variant="outline" className="h-8 text-xs">
                                Create a join code
                            </Button>
                        </div>
                    )}
                </StepCard>

                {/* 6 — key dates */}
                <StepCard
                    icon={CalendarClock}
                    n={6}
                    title="Add your company's key dates"
                    why="Your filing deadlines are worked out from these. Leave them blank and we simply won't show those deadlines — we never guess a date."
                    state={dates.company_review_date || dates.accounting_reference_date ? "done" : claimed ? "active" : "locked"}
                >
                    {claimed && (
                        <div className="flex flex-wrap gap-3 items-end">
                            <div>
                                <Label htmlFor="crd">Confirmation statement review date</Label>
                                <Input
                                    id="crd"
                                    type="date"
                                    value={dates.company_review_date}
                                    onChange={(e) => setDates({ ...dates, company_review_date: e.target.value })}
                                    data-testid="company-review-date-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="ard">Accounting reference date</Label>
                                <Input
                                    id="ard"
                                    type="date"
                                    value={dates.accounting_reference_date}
                                    onChange={(e) => setDates({ ...dates, accounting_reference_date: e.target.value })}
                                    data-testid="accounting-reference-date-input"
                                />
                            </div>
                            <Button
                                onClick={saveDates}
                                disabled={busy}
                                className="h-9 text-xs bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid="save-dates-button"
                            >
                                Save dates
                            </Button>
                            <p className="text-xs text-slate-500 w-full">
                                Both are on your Companies House record. They're different from the service
                                charge year.
                            </p>
                        </div>
                    )}
                </StepCard>
            </div>

            <div className="mt-8 rounded-md bg-slate-50 border border-slate-200 p-4 flex gap-3">
                <Lock size={16} className="text-slate-500 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600 leading-relaxed">
                    <strong className="text-slate-900">What stays private.</strong> Your deeds, your bills
                    and your correspondence are yours. They're never shared with your neighbours, your
                    local area, or anyone else — regardless of the settings above. Only reference material
                    like council regulations can ever leave your estate, and only when a director chooses
                    to share it.
                </p>
            </div>

            <div className="mt-6 flex items-center gap-3">
                <Button
                    onClick={() => navigate("/money")}
                    className="text-xs h-9 bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="onboarding-next-money"
                >
                    Next: add your service charge statement
                </Button>
                <Button variant="outline" onClick={() => navigate("/")} className="text-xs h-8">
                    Go to my dashboard
                </Button>
            </div>
        </div>
    );
}
